import { useCallback, useEffect, useRef } from "react";
import { useSseStore } from "@/modules/store/sseSliceStore";
import { AppError } from "@/lib/utils/errors";
import { useBusinessInfo } from "@/modules/hooks/business";
import { sseService } from "@/modules/services/realtime/sseService";
import { useNotificationStore } from "@/modules/store/notificationStore";
import { NotificationMessageDto } from "@/modules/types/notification";
import useAllowNoti from "./useAllowNoti";
import { usePathname, useRouter } from "next/navigation";
import { MessageOverEvent } from "@/modules/types/conversation/message";
import { routes } from "@/lib/utils/routes";
import { enqueueSnackbar } from "notistack";
import { useMessage } from "../conversations/useMessage";

interface SSEHookResult {
  isConnected: boolean;
  error: string | null;
  messages: any[];
}

const useSSEConnection = (): SSEHookResult => {
  const {
    isConnected,
    isConnecting,
    errorMessage: error,
    messages,
    lastEventId,
    connectSSEAsync,
    disconnect,
    clearMessages,
  } = useSseStore();

  const { appendMessage } = useMessage();
  const { appendNotReadNoti } = useNotificationStore();
  const { businessInfo } = useBusinessInfo();
  const { isAllowed } = useAllowNoti();
  const router = useRouter();
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);

  const reconnectAttemptRef = useRef<number>(0);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const maxReconnectDelay = 30000; // Maximum backoff of 30 seconds
  const initialReconnectDelay = 1000; // Start with 1 second delay
  const wasConnectedRef = useRef<boolean>(false);
  const shouldContinueReconnectingRef = useRef<boolean>(false);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  const handleConnect = useCallback(
    (businessId: string) => {
      if (!businessId) return;

      const url = sseService.getSseEndpoint(businessId);
      connectSSEAsync({ url, lastEventId });
    },
    [connectSSEAsync, lastEventId],
  );

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    // Don't schedule if we're already connecting or connected
    if (isConnected || isConnecting) {
      return;
    }

    clearReconnectTimer();

    // Calculate exponential backoff with jitter
    const attempt = reconnectAttemptRef.current;
    const delay = Math.min(
      initialReconnectDelay * Math.pow(2, Math.min(attempt, 10)), // Cap the exponent to avoid overflow
      maxReconnectDelay,
    );
    const jitter = delay * 0.2 * Math.random();
    const reconnectDelay = Math.floor(delay + jitter);

    reconnectTimerRef.current = setTimeout(() => {
      // Double-check we're not already connecting before attempting
      if (businessInfo?.id && !isConnected && !isConnecting) {
        reconnectAttemptRef.current += 1;
        handleConnect(businessInfo.id);
      }
    }, reconnectDelay);
  }, [
    businessInfo,
    isConnected,
    isConnecting,
    handleConnect,
    clearReconnectTimer,
  ]);

  useEffect(() => {
    if (businessInfo?.id && !isConnected && !isConnecting) {
      reconnectAttemptRef.current = 0; // Reset attempts for initial connection
      handleConnect(businessInfo.id);
    }

    return () => {
      clearReconnectTimer();
      if (isConnected || isConnecting) {
        disconnect();
        clearMessages();
      }
    };
  }, [businessInfo?.id]);

  useEffect(() => {
    // If we just connected
    if (isConnected) {
      reconnectAttemptRef.current = 0;
      clearReconnectTimer();
      wasConnectedRef.current = true;
      shouldContinueReconnectingRef.current = false;
    }
    // If we were connected and now we're not
    else if (wasConnectedRef.current && !isConnected && !isConnecting) {
      wasConnectedRef.current = false;
      shouldContinueReconnectingRef.current = true;
      scheduleReconnect();
    }
  }, [isConnected, isConnecting, scheduleReconnect, clearReconnectTimer]);

  useEffect(() => {
    // If we tried connecting and it failed (we're no longer connecting and not connected)
    if (
      !isConnected &&
      !isConnecting &&
      (error || shouldContinueReconnectingRef.current)
    ) {
      if (error) {
        const appErr = new AppError(error);
        appErr.logError();
      }

      // Only schedule if we haven't already scheduled a reconnect
      if (!reconnectTimerRef.current) {
        shouldContinueReconnectingRef.current = true;
        scheduleReconnect();
      }
    }
  }, [isConnected, isConnecting, error, scheduleReconnect]);

  useEffect(() => {
    // When network comes back online, retry immediately
    const handleOnline = () => {
      if (!isConnected && !isConnecting && businessInfo?.id) {
        clearReconnectTimer();
        // Wait a short moment for network to stabilize
        setTimeout(() => {
          handleConnect(businessInfo.id);
        }, 1000);
      }
    };

    // When network goes offline, note the disconnection but don't try to reconnect yet
    const handleOffline = () => {
      shouldContinueReconnectingRef.current = true;
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [
    businessInfo,
    isConnected,
    isConnecting,
    handleConnect,
    clearReconnectTimer,
  ]);

  useEffect(() => {
    // Example: Process received messages
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      // Handle the message based on its type, e.g., show notification, update UI
      switch (latestMessage.type) {
        case "notification":
          notifyNotification(latestMessage.data as NotificationMessageDto);
          break;
        case "new_message":
          notifyNewMessage(latestMessage.data as MessageOverEvent);
          break;
      }
    }
  }, [messages]); // Run when messages array changes

  const notifyNotification = (noti: NotificationMessageDto) => {
    appendNotReadNoti([noti]);
    enqueueSnackbar(noti.title || "Bạn có thông báo mới", {
      autoHideDuration: 10000,
      // @ts-expect-error just add notification variant
      variant: "notification",
      notification: noti,
    });
    if (isAllowed) {
      const notification = new Notification(
        noti.title || "Bạn có thông báo mới",
        {
          icon: noti.subject?.[0].image,
          body: noti.content,
        },
      );
      notification.onclick = () => {
        if (noti.actionUrl) {
          router.push(noti.actionUrl);
        } else {
          window.focus();
        }
      };
    }
  };

  const notifyNewMessage = (message: MessageOverEvent) => {
    appendMessage(message.conversationId, message);
    if (message.senderName === businessInfo?.companyName) {
      return;
    }
    if (pathnameRef.current == `${routes.messages}/${message.conversationId}`) {
      return;
    }
    enqueueSnackbar(`${message.senderName} đã gửi tin nhắn mới`, {
      autoHideDuration: 10000,
      // @ts-expect-error just add notification variant
      variant: "notification",
      notification: message,
    });
    if (isAllowed && businessInfo) {
      if (
        message.senderName == businessInfo.companyName ||
        message.senderName == businessInfo.shortCompanyName
      ) {
        return;
      }
      const notification = new Notification(
        `${message.senderName} đã gửi tin nhắn mới`,
        {
          icon: message.image,
          body: message.content,
        },
      );
      notification.onclick = () => {
        if (message.actionUrl && message.actionUrl.length > 0) {
          router.push(message.actionUrl);
        } else {
          window.focus();
        }
      };
    }
  };

  return { isConnected, messages, error };
};

export default useSSEConnection;
